import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ApprovalStatus, StatusOption } from "@/lib/api-types";

// ── Watch-scoped mutations ────────────────────────────────────────────────────

// ── Shared shapes ────────────────────────────────────────────────────────────

/**
 * One video version — matches the shape returned by both
 * GET /api/editor/videos/:id/versions and GET /api/watch/:token/versions.
 */
export type WatchVersion = {
  id: string;
  title?: string;
  versionNumber: number;
  assetType?: "video" | "audio" | "image";
  approvalStatus?: ApprovalStatus;
  fileSizeBytes?: number;
  createdAt?: string;
  cdnUrl?: string | null;
  /** External-link assets (migration 0024). 'upload' = hosted file (use cdnUrl). */
  sourceType?: "upload" | "youtube" | "vimeo";
  sourceVideoId?: string | null;
  sourceUrl?: string | null;
  /** Populated ONLY on the stack root when a share link exists. */
  shareToken?: string | null;
  /** Populated ONLY on the stack root — powers the Share dialog's download toggle. */
  downloadAllowed?: boolean | null;
};

/**
 * Workspace watch data — /versions endpoint is the anchor here since it also
 * returns the stack root id + all sibling versions. Additional details (title,
 * approvalStatus for the currently-viewed version) are read from the versions
 * list itself — there's no separate video-detail endpoint on the workspace side.
 */
export type WorkspaceWatchData = {
  rootVideoId: string;
  /** Owning project id — powers the Back fallback on direct load / refresh. */
  projectId: string | null;
  /** Unified Status field (migration 0023) — workspace option list + this asset's status. */
  statusOptions?: StatusOption[];
  status?: StatusOption | null;
  versions: WatchVersion[];
};

/**
 * Guest watch data — GET /api/watch/:token response.
 * skipForm=false means the visitor cookie is missing/expired → identify form must run.
 */
export type GuestWatchData = {
  ok: true;
  skipForm: boolean;
  rootVideoId: string;
  video: {
    id: string;
    title: string;
    assetType?: "video" | "audio" | "image";
    versionNumber: number;
    cdnUrl?: string | null;
    sourceType?: "upload" | "youtube" | "vimeo";
    sourceVideoId?: string | null;
    sourceUrl?: string | null;
    downloadAllowed?: boolean;
    approvalStatus?: ApprovalStatus;
    approvalUpdatedAt?: string;
  };
  versions: WatchVersion[];
  adminName?: string;
  statusOptions?: StatusOption[];
  status?: StatusOption | null;
};

// ── Workspace mode ───────────────────────────────────────────────────────────

/**
 * Workspace stack fetch — accepts any video id in the stack, returns root + all versions.
 * Uses the existing versions endpoint as the primary loader for the watch page.
 */
export function useWorkspaceWatchData(videoId: string | undefined) {
  return useQuery<WorkspaceWatchData>({
    queryKey: ["watch", "workspace", videoId],
    queryFn: () => api.get<WorkspaceWatchData>(`/editor/videos/${videoId}/versions`),
    enabled: !!videoId,
    staleTime: 15_000,
  });
}

/**
 * POST /api/editor/videos/:id/view — records "Seen By" for the current user.
 * Fire-and-forget on watch page mount. Non-blocking — failures are silent.
 */
export function useRegisterWorkspaceView() {
  return useMutation({
    mutationFn: (videoId: string) =>
      api.post<{ ok: true }>(`/editor/videos/${videoId}/view`),
  });
}

/**
 * GET /api/editor/videos/:id/views — Seen By list for Fields tab.
 * Returns unified list of workspace members + guests who have viewed any version.
 */
export type Viewer = {
  type: "workspace_member" | "guest";
  name: string;
  email?: string | null;
  role?: string;
  viewedAt: string;
};

export function useVideoViews(videoId: string | undefined) {
  return useQuery<{ videoId: string; viewers: Viewer[] }>({
    queryKey: ["watch", "views", videoId],
    queryFn: () => api.get(`/editor/videos/${videoId}/views`),
    enabled: !!videoId,
    staleTime: 30_000,
  });
}

// ── Guest mode ───────────────────────────────────────────────────────────────

/**
 * GET /api/watch/:token — primary loader for the guest watch page.
 * Sets `skipForm` = false when the visitor cookie is missing/expired, so the
 * page can render the name+email identify form instead of the player.
 */
export function useGuestWatchData(token: string | undefined) {
  return useQuery<GuestWatchData>({
    queryKey: ["watch", "guest", token],
    queryFn: () => api.get<GuestWatchData>(`/watch/${token}`),
    enabled: !!token,
    staleTime: 15_000,
    // Do NOT retry on 401 — that means identify is needed, not a transient failure.
    retry: false,
  });
}

// ── Comments (shared shape) ──────────────────────────────────────────────────

/**
 * Comment shape matches `formatComment()` in worker/lib/comments.js.
 * Identical between workspace and guest endpoints (both LEFT JOIN through the
 * same helper), so a single type covers both.
 */
export type WatchComment = {
  id: string;
  videoId: string;
  content: string;
  timestampSeconds: number | null;
  positionX: number | null;
  positionY: number | null;
  createdAt: string;
  author: {
    type: "workspace" | "guest";
    /** Workspace member id when type='workspace'; null for guests. Reliable owner check. */
    userId: string | null;
    name: string | null;
    role: string | null;
  };
  commentedOnVersionNumber: number | null;
  resolved: boolean;
  resolvedAt: string | null;
  resolvedBy: {
    userId: string | null;
    name: string | null;
    role: string | null;
    onVersionNumber: number | null;
  } | null;
  annotationsUrl: string | null;
  /** 'public' (default) or 'internal' — workspace-only. */
  visibility?: "public" | "internal";
  /**
   * Threading — NULL for root comments, id-of-parent for replies.
   * Backend caps depth at 1, so this always points to a root comment.
   */
  parentCommentId: string | null;
};

/**
 * GET /api/editor/videos/:id/comments (workspace, stack-wide) OR
 * GET /api/watch/:token/comments (guest). Backend returns stack-wide comments
 * — any version's comments show up alongside every other version in the stack.
 */
export function useWatchComments(
  mode: "workspace" | "guest" | "share",
  key: string | undefined,
  /** REQUIRED when mode="share" — the current video id (share comment endpoint is per-video). */
  currentVideoId?: string | undefined,
) {
  return useQuery<WatchComment[]>({
    queryKey: ["watch", "comments", mode, key, mode === "share" ? currentVideoId : undefined],
    queryFn: () => {
      if (mode === "workspace") return api.get<WatchComment[]>(`/editor/videos/${key}/comments`);
      if (mode === "share") {
        return api.get<WatchComment[]>(`/share/${key}/videos/${currentVideoId}/comments`);
      }
      return api.get<WatchComment[]>(`/watch/${key}/comments`);
    },
    enabled: !!key && (mode !== "share" || !!currentVideoId),
    staleTime: 5_000,
    // Near-real-time comments without a WebSocket: poll every 8s, but only
    // while the tab is focused (no background polling), plus refetch when the
    // viewer returns to the tab. Covers workspace/guest/share (shared query).
    // True push (Durable Object WS rooms) is a phase-2 item.
    refetchInterval: 8_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * PATCH the approval status on the currently-viewed version. Workspace hits
 * `/editor/videos/:id/approval`; guest hits `/watch/:token/approval` with
 * `videoId` in the body so the write targets the same version the viewer sees.
 * Invalidates watch queries so the Fields tab + top-bar reflect the new status.
 */
export function useUpdateWatchApproval(mode: "workspace" | "guest" | "share", watchKey: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      videoId,
      approvalStatus,
    }: {
      videoId: string;
      approvalStatus: ApprovalStatus;
    }) => {
      if (mode === "workspace") {
        return api.patch(`/editor/videos/${videoId}/approval`, { approvalStatus });
      }
      if (mode === "share") {
        return api.patch(`/share/${watchKey}/videos/${videoId}/approval`, { approvalStatus, videoId });
      }
      return api.patch(`/watch/${watchKey}/approval`, { approvalStatus, videoId });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["watch", mode, watchKey] });
      qc.invalidateQueries({ queryKey: ["watch", "workspace", watchKey] });
    },
  });
}

/**
 * PATCH the unified Status on the asset (resolves to the stack root server-side).
 * Workspace → /editor/videos/:id/status; share → /share/:token/videos/:id/status;
 * guest → /watch/:token/status. Body: { statusOptionId } or { label } (create-or-reuse).
 */
export function useSetWatchStatus(mode: "workspace" | "guest" | "share", watchKey: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      videoId,
      statusOptionId,
      label,
    }: {
      videoId?: string;
      statusOptionId?: string;
      label?: string;
    }) => {
      const body = statusOptionId ? { statusOptionId } : { label };
      if (mode === "workspace") return api.patch(`/editor/videos/${videoId}/status`, body);
      if (mode === "share") return api.patch(`/share/${watchKey}/videos/${videoId}/status`, body);
      return api.patch(`/watch/${watchKey}/status`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["watch", mode, watchKey] });
      qc.invalidateQueries({ queryKey: ["watch", "workspace", watchKey] });
      qc.invalidateQueries({ queryKey: ["share"] });
      qc.invalidateQueries({ queryKey: ["status-options"] });
    },
  });
}

/**
 * POST a new comment. Workspace endpoint scopes by videoId (path param); the
 * guest endpoint scopes by token and takes the target videoId in the body.
 * `timestampSeconds` may be null → "timestamp off" comment (general note).
 * Task 12/13 will pass `annotations` and `positionX/Y` through this same hook.
 */
export function useCreateComment(mode: "workspace" | "guest" | "share", watchKey: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      currentVersionId: string;
      content: string;
      timestampSeconds: number | null;
      positionX?: number | null;
      positionY?: number | null;
      annotations?: { shapes: unknown[] } | null;
      visibility?: "public" | "internal";
      /** Reply target — id of the ROOT comment being replied to. Backend rejects replies to replies. */
      parentCommentId?: string | null;
    }) => {
      const { currentVersionId, ...rest } = body;
      if (mode === "workspace") {
        return api.post(`/editor/videos/${currentVersionId}/comments`, rest);
      }
      if (mode === "share") {
        // Share comment endpoint is per-video (path scoped); videoId in body is
        // also accepted by the backend but redundant here.
        return api.post(`/share/${watchKey}/videos/${currentVersionId}/comments`, {
          ...rest,
          videoId: currentVersionId,
        });
      }
      return api.post(`/watch/${watchKey}/comments`, { ...rest, videoId: currentVersionId });
    },
    onSuccess: () => {
      // Comments are STACK-WIDE server-side, but useWatchComments keys per
      // videoId so switching versions creates a new cache entry. A single
      // invalidation of ["watch","comments",mode,watchKey] would miss the
      // sibling-version entries and leave them stale for the 5s staleTime.
      // Match every comment cache for this mode instead — one predicate
      // fires across all cached versions in the same stack.
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "watch" &&
          q.queryKey[1] === "comments" &&
          q.queryKey[2] === mode,
      });
    },
  });
}

/**
 * PATCH /api/editor/comments/:id — author-only text edit (Task 2 backend).
 * Rejects annotated/anchor/drawn comments server-side. Body: `{ content }`.
 * `watchKey` param is retained for signature compatibility with callers;
 * invalidation is now predicate-based across every workspace-comments cache
 * so we don't need it here.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useEditComment(_watchKey: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      api.patch<{ ok: true; id: string; content: string }>(`/editor/comments/${id}`, { content }),
    onSuccess: () => {
      // Same reason as useCreateComment — match every workspace comment
      // cache so sibling-version entries don't serve a stale edit.
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "watch" &&
          q.queryKey[1] === "comments" &&
          q.queryKey[2] === "workspace",
      });
    },
  });
}

/**
 * DELETE comment. Workspace route hits /editor/comments/:id (author or ADMIN).
 * Guest route hits /watch/:token/comments/:id and is limited server-side to
 * the guest's own comment.
 */
export function useDeleteComment(
  mode: "workspace" | "guest" | "share",
  watchKey: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (mode === "workspace") {
        return api.delete<{ ok: true }>(`/editor/comments/${id}`);
      }
      if (mode === "share") {
        return api.delete<{ ok: true }>(`/share/${watchKey}/comments/${id}`);
      }
      return api.delete<{ ok: true }>(`/watch/${watchKey}/comments/${id}`);
    },
    onSuccess: () => {
      // Comments are STACK-WIDE server-side, but useWatchComments keys per
      // videoId so switching versions creates a new cache entry. A single
      // invalidation of ["watch","comments",mode,watchKey] would miss the
      // sibling-version entries and leave them stale for the 5s staleTime.
      // Match every comment cache for this mode instead — one predicate
      // fires across all cached versions in the same stack.
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "watch" &&
          q.queryKey[1] === "comments" &&
          q.queryKey[2] === mode,
      });
    },
  });
}

/**
 * PATCH resolve. Workspace: any workspace member can resolve any stack comment.
 * Guest: any identified guest can resolve any PUBLIC stack comment (Frame.io
 * behavior; internal comments are workspace-scoped and hidden from guests).
 * Body: `{ resolvedByVideoId }` — the version being viewed at resolve time.
 */
export function useResolveComment(
  mode: "workspace" | "guest" | "share",
  watchKey: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolvedByVideoId }: { id: string; resolvedByVideoId: string }) => {
      if (mode === "workspace") {
        return api.patch(`/editor/comments/${id}/resolve`, { resolvedByVideoId });
      }
      if (mode === "share") {
        return api.patch(`/share/${watchKey}/comments/${id}/resolve`, { resolvedByVideoId });
      }
      return api.patch(`/watch/${watchKey}/comments/${id}/resolve`, { resolvedByVideoId });
    },
    onSuccess: () => {
      // Comments are STACK-WIDE server-side, but useWatchComments keys per
      // videoId so switching versions creates a new cache entry. A single
      // invalidation of ["watch","comments",mode,watchKey] would miss the
      // sibling-version entries and leave them stale for the 5s staleTime.
      // Match every comment cache for this mode instead — one predicate
      // fires across all cached versions in the same stack.
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "watch" &&
          q.queryKey[1] === "comments" &&
          q.queryKey[2] === mode,
      });
    },
  });
}

/** PATCH unresolve — same mode split as useResolveComment. */
export function useUnresolveComment(
  mode: "workspace" | "guest" | "share",
  watchKey: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (mode === "workspace") {
        return api.patch(`/editor/comments/${id}/unresolve`);
      }
      if (mode === "share") {
        return api.patch(`/share/${watchKey}/comments/${id}/unresolve`);
      }
      return api.patch(`/watch/${watchKey}/comments/${id}/unresolve`);
    },
    onSuccess: () => {
      // Comments are STACK-WIDE server-side, but useWatchComments keys per
      // videoId so switching versions creates a new cache entry. A single
      // invalidation of ["watch","comments",mode,watchKey] would miss the
      // sibling-version entries and leave them stale for the 5s staleTime.
      // Match every comment cache for this mode instead — one predicate
      // fires across all cached versions in the same stack.
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "watch" &&
          q.queryKey[1] === "comments" &&
          q.queryKey[2] === mode,
      });
    },
  });
}

/** POST /api/watch/:token/identify — creates visitor + guest for this share stack. */
export function useIdentifyGuest(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; email: string }) =>
      api.post<{ ok: true }>(`/watch/${token}/identify`, body),
    onSuccess: () => {
      // Re-fetch the main watch payload — now skipForm will be true and player renders.
      qc.invalidateQueries({ queryKey: ["watch", "guest", token] });
    },
  });
}

/**
 * POST /api/watch/:token/view — records "Seen By" for the identified guest.
 * Fire-and-forget on watch page mount (after identify succeeds). Non-blocking.
 */
export function useRegisterGuestView() {
  return useMutation({
    mutationFn: (token: string) =>
      api.post<{ ok: true }>(`/watch/${token}/view`),
  });
}

// ── Convenience: auto-register view on mount ─────────────────────────────────

/**
 * Fires a view-record POST once when the given key is ready. Silently no-ops
 * if key is undefined. Safe to call unconditionally at the top of the page.
 *
 * For mode="share", `key` is the share token and `videoId` is REQUIRED — the
 * share view endpoint is per-video (`/api/share/:token/videos/:videoId/view`)
 * so the workspace/guest signature isn't enough on its own.
 */
export function useAutoRegisterView(
  mode: "workspace" | "guest" | "share",
  key: string | undefined,
  ready: boolean,
  videoId?: string | undefined,
) {
  const workspace = useRegisterWorkspaceView();
  const guest = useRegisterGuestView();
  const shareView = useRegisterShareView();

  useEffect(() => {
    if (!ready || !key) return;
    if (mode === "workspace") {
      workspace.mutate(key);
    } else if (mode === "share") {
      if (!videoId) return;
      shareView.mutate({ token: key, videoId });
    } else {
      guest.mutate(key);
    }
    // Only fire once per (mode, key, videoId) — mutations are stable, safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, key, ready, videoId]);
}

/** POST /api/share/:token/videos/:videoId/view — Share-scoped view register. */
export function useRegisterShareView() {
  return useMutation({
    mutationFn: ({ token, videoId }: { token: string; videoId: string }) =>
      api.post<{ ok: true }>(`/share/${token}/videos/${videoId}/view`),
  });
}
